// The BOOKING calendar (ART-331, Touring → Calendar on the Booking lens): the agency's deals laid out by day — the
// same five states its Active deals board shows (confirmed, Ready to confirm, hold, waiting on artist, soft hold),
// for one artist or the whole roster. Full width: a type filter above the month, one chip per deal (soft holds on
// one night for one artist collapse into "Soft hold ×N"), and a pop-up per chip carrying the board's own verbs.
//
// The verbs run through `RosterDealModal`, which mounts the deal's own band — so they work the same from the roster
// scope (no band mounted) as from one artist's page, and they are the board's calls, not a second set.
import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Icon, Page, Spinner } from "@cntrl/ui";
import { useOrgContextOrNull } from "../../app/orgContext.js";
import {
  useArtistBookingCalendar,
  useRosterBookingCalendar,
  type BookingCalendarItem,
  type BookingCalendarRead,
} from "../../app/hooks/useBookingCalendar.js";
import { useResolvedOrg } from "../../app/hooks/useRoster.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { initials } from "../../shells/mobile/accountRoles.js";
import { DetailsFrame } from "../booking/DetailsFrame.js";
import { ArtistMark } from "./ArtistMark.js";
import { RosterDealModal, type RosterDealTarget } from "./RosterDealModal.js";
import { formatDay, offerDaysLeft, offerDealLine } from "./rosterFormat.js";
import { ago } from "./bookingFunnelCards.js";
import base from "../booking/CalendarEventDetails.module.scss";
import styles from "./BookingCalendarScreen.module.scss";

type Kind = "confirmed" | "ready" | "hold" | "requested" | "soft";

/** The filter pills, in the board's own order of urgency. `offer` (the board's Offer column) means both pre-hold
 *  states together. */
const FILTERS: { key: string; label: string; kinds: Kind[] }[] = [
  { key: "all", label: "All", kinds: ["confirmed", "ready", "hold", "requested", "soft"] },
  { key: "confirmed", label: "Confirmed", kinds: ["confirmed"] },
  { key: "ready", label: "Ready to confirm", kinds: ["ready"] },
  { key: "hold", label: "Hold", kinds: ["hold"] },
  { key: "requested", label: "Waiting on artist", kinds: ["requested"] },
  { key: "soft", label: "Soft hold", kinds: ["soft"] },
];

const STATE_LABEL: Record<Kind, string> = {
  confirmed: "Confirmed",
  ready: "Ready to confirm",
  hold: "Hold",
  requested: "Waiting on artist",
  soft: "Soft hold",
};

const HOLD_TIER = ["", "1st hold", "2nd hold", "3rd hold"];
/** Good through turns red at this many days or fewer (the design's rule for this surface). */
const URGENT_DAYS = 5;

/** Which kinds a URL `type` shows. */
function kindsForType(type: string | undefined): Kind[] {
  if (type === "offer") return ["requested", "soft"];
  return (FILTERS.find((f) => f.key === type) ?? FILTERS[0]!).kinds;
}

export interface BookingCalendarScreenProps {
  scope: "artist" | "roster";
  /** `?type=` — all | confirmed | ready | hold | requested | soft | offer. */
  type?: string;
  onTypeChange: (type: string | undefined) => void;
}

export function BookingCalendarScreen(props: BookingCalendarScreenProps) {
  return props.scope === "artist" ? <ArtistCalendar {...props} /> : <RosterCalendar {...props} />;
}

function ArtistCalendar(props: BookingCalendarScreenProps) {
  const [cursor, setCursor] = useState(() => monthStart(new Date()));
  const { from, to } = gridWindow(cursor);
  const q = useArtistBookingCalendar(from, to);
  return <CalendarView {...props} cursor={cursor} setCursor={setCursor} read={q.data} loading={q.isLoading} refetch={() => void q.refetch()} />;
}

function RosterCalendar(props: BookingCalendarScreenProps) {
  const org = useOrgContextOrNull();
  const { orgId: resolved } = useResolvedOrg();
  const orgId = org?.orgId ?? resolved;
  const [cursor, setCursor] = useState(() => monthStart(new Date()));
  const { from, to } = gridWindow(cursor);
  const q = useRosterBookingCalendar(orgId, from, to);
  return (
    <Page title="All roster" subtitle="Booking calendar">
      <CalendarView {...props} cursor={cursor} setCursor={setCursor} read={q.data} loading={q.isLoading} refetch={() => void q.refetch()} />
    </Page>
  );
}

/** A chip on a day: one deal, or one artist's soft holds that night grouped. */
interface Chip {
  key: string;
  kind: Kind;
  items: BookingCalendarItem[];
}

function CalendarView({ scope, type, onTypeChange, cursor, setCursor, read, loading, refetch }: BookingCalendarScreenProps & {
  cursor: Date;
  setCursor: (d: Date) => void;
  read: BookingCalendarRead | undefined;
  loading: boolean;
  refetch: () => void;
}) {
  const [open, setOpen] = useState<{ chip: Chip; anchor: DOMRect } | null>(null);
  const [target, setTarget] = useState<RosterDealTarget | null>(null);
  const active = useMemo(() => kindsForType(type), [type]);
  const items = useMemo(() => read?.items ?? [], [read]);
  const monthKey = isoDay(cursor).slice(0, 7);

  // Counts are for the month on screen — what the pills filter.
  const counts = useMemo(() => {
    const inMonth = items.filter((i) => (i.date ?? "").startsWith(monthKey));
    return Object.fromEntries(FILTERS.map((f) => [f.key, inMonth.filter((i) => f.kinds.includes(i.kind as Kind)).length]));
  }, [items, monthKey]);

  const chipsByDay = useMemo(() => {
    const map = new Map<string, Chip[]>();
    const shown = items.filter((i) => active.includes(i.kind as Kind));
    for (const i of shown) {
      const day = (i.date ?? "").slice(0, 10);
      const list = map.get(day) ?? [];
      if (i.kind === "soft") {
        const existing = list.find((c) => c.kind === "soft" && c.items[0]?.bandId === i.bandId);
        if (existing) {
          existing.items.push(i);
          map.set(day, list);
          continue;
        }
      }
      list.push({ key: i.offerId ?? i.showId ?? `${day}-${list.length}`, kind: i.kind as Kind, items: [i] });
      map.set(day, list);
    }
    return map;
  }, [items, active]);

  const days = gridDays(cursor);
  const today = isoDay(new Date());

  function choose(key: string) {
    onTypeChange(key === "all" ? undefined : key);
  }
  const isOn = (key: string) =>
    key === "all" ? !type || type === "all" : type === key || (type === "offer" && (key === "requested" || key === "soft"));

  return (
    <div className={styles.page}>
      <div className={styles.filters} role="group" aria-label="Show">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={styles.filter}
            data-on={isOn(f.key) || undefined}
            aria-pressed={isOn(f.key)}
            onClick={() => choose(f.key)}
          >
            {f.key !== "all" && <span className={styles.swatch} data-kind={f.key} aria-hidden />}
            {f.label}
            <span className={styles.filterCount}>{counts[f.key] ?? 0}</span>
          </button>
        ))}
      </div>

      <section className={styles.card} aria-label="Calendar">
        <header className={styles.cardHead}>
          <h2 className={styles.month}>
            {cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
          </h2>
          {loading && <Spinner size={16} label="Loading deals" />}
          <div className={styles.nav}>
            <button type="button" className={styles.navBtn} aria-label="Previous month" onClick={() => setCursor(addMonths(cursor, -1))}>
              <Icon icon={ChevronLeft} size={16} />
            </button>
            <button type="button" className={styles.today} onClick={() => setCursor(monthStart(new Date()))}>Today</button>
            <button type="button" className={styles.navBtn} aria-label="Next month" onClick={() => setCursor(addMonths(cursor, 1))}>
              <Icon icon={ChevronRight} size={16} />
            </button>
          </div>
        </header>

        <div className={styles.grid} role="grid">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <span key={i} className={styles.dow} aria-hidden>{d}</span>
          ))}
          {days.map((d) => {
            const key = isoDay(d);
            const outside = d.getMonth() !== cursor.getMonth();
            const chips = chipsByDay.get(key) ?? [];
            return (
              <div key={key} className={styles.day} data-outside={outside || undefined} data-today={key === today || undefined}>
                <span className={styles.dayNum}>{d.getDate()}</span>
                {chips.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className={styles.chip}
                    data-kind={c.kind}
                    onClick={(e) => setOpen({ chip: c, anchor: e.currentTarget.getBoundingClientRect() })}
                    aria-label={`${STATE_LABEL[c.kind]} — ${chipLabel(c)}${scope === "roster" ? ` — ${c.items[0]?.artistName ?? ""}` : ""}`}
                  >
                    <span className={styles.chipAvatar} aria-hidden>
                      <ArtistMark name={c.items[0]?.artistName} bandId={c.items[0]?.bandId} avatarMediaId={c.items[0]?.artistAvatarMediaId} size="xs" />
                    </span>
                    <span className={styles.chipText}>{chipLabel(c)}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>

        <p className={styles.footer}>Soft holds are only visible to {read?.agency ?? "your agency"}</p>
      </section>

      {open && (
        <DealPopover
          chip={open.chip}
          anchor={open.anchor}
          agency={read?.agency ?? "your agency"}
          onClose={() => setOpen(null)}
          onAct={(t) => {
            setOpen(null);
            setTarget(t);
          }}
        />
      )}
      <RosterDealModal
        target={target}
        onClose={() => {
          setTarget(null);
          refetch();
        }}
      />
    </div>
  );
}

function chipLabel(c: Chip): string {
  if (c.kind === "soft" && c.items.length > 1) return `Soft hold ×${c.items.length}`;
  return c.items[0]?.venue?.trim() || "TBD";
}

/** One deal's pop-up: the state, who and where, the deal, and the board's verbs for that state. */
function DealPopover({ chip, anchor, agency, onClose, onAct }: {
  chip: Chip;
  anchor: DOMRect;
  agency: string;
  onClose: () => void;
  onAct: (target: RosterDealTarget) => void;
}) {
  const i = chip.items[0]!;
  const group = chip.kind === "soft" && chip.items.length > 1;
  const label = [i.venue?.trim() || "TBD", formatDay(i.date)].join(" · ");
  const where = [i.city, i.state].filter(Boolean).join(", ");
  const when = [
    new Date(`${(i.date ?? "").slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
    where || null,
  ].filter(Boolean).join(" · ");
  const days = offerDaysLeft(i.goodThrough);
  const team = i.team ?? [];
  const inCount = team.filter((p) => p.status === "in").length;

  const rows: { k: string; v: string; urgent?: boolean }[] = [];
  const deal = offerDealLine(i.amountCents ?? null, i.dealType ?? "guarantee", i.doorSplitPct ?? null);
  if (deal) rows.push({ k: "Deal", v: deal });
  if ((chip.kind === "hold" || chip.kind === "ready" || chip.kind === "soft" || chip.kind === "requested") && i.holdTier)
    rows.push({ k: "Hold level", v: HOLD_TIER[i.holdTier] ?? `${i.holdTier}th hold` });
  if (chip.kind !== "confirmed" && i.goodThrough)
    rows.push({
      k: "Good through",
      v: `${formatDay(i.goodThrough)}${days != null ? (days < 0 ? " · passed" : ` · ${days}d left`) : ""}`,
      urgent: days != null && days <= URGENT_DAYS,
    });
  if (chip.kind === "requested") rows.push({ k: "Status", v: ["Sent to the team", ago(i.sentOn)].filter(Boolean).join(" · ") });
  if (chip.kind === "ready")
    rows.push({ k: "Status", v: i.decidedBy ? `Approved by ${i.decidedBy} · finish the deal` : "Team is in · finish the deal with the buyer" });
  if (chip.kind === "soft") rows.push({ k: "Visible to", v: `${agency} only` });

  // `danger` is the red-text outline verb; `brand` is the red fill (Confirm show, per the design).
  const actions: { label: string; tone?: "primary" | "danger" | "brand"; onClick: () => void }[] = [];
  if (chip.kind === "soft" && group) {
    actions.push({
      label: "Release all", tone: "danger",
      onClick: () => onAct({ kind: "release", bandId: i.bandId!, offerIds: chip.items.map((x) => x.offerId!), label: formatDay(i.date) }),
    });
    actions.push({ label: "Open", tone: "primary", onClick: () => onAct({ kind: "offer", bandId: i.bandId!, offerId: i.offerId! }) });
  } else if (chip.kind === "soft") {
    actions.push({ label: "Release", tone: "danger", onClick: () => onAct({ kind: "release", bandId: i.bandId!, offerIds: [i.offerId!], label }) });
    actions.push({ label: "Send to team", tone: "primary", onClick: () => onAct({ kind: "send", bandId: i.bandId!, offerId: i.offerId! }) });
  } else if (chip.kind === "requested") {
    actions.push({ label: "Edit offer", tone: "primary", onClick: () => onAct({ kind: "offer", bandId: i.bandId!, offerId: i.offerId! }) });
  } else if (chip.kind === "hold") {
    actions.push({ label: "Open hold", tone: "primary", onClick: () => onAct({ kind: "hold", bandId: i.bandId!, showId: i.showId! }) });
  } else if (chip.kind === "ready") {
    actions.push({ label: "Cancel show", tone: "danger", onClick: () => onAct({ kind: "cancel", bandId: i.bandId!, showId: i.showId!, label }) });
    actions.push({ label: "Confirm show", tone: "brand", onClick: () => onAct({ kind: "confirm", bandId: i.bandId!, showId: i.showId!, label }) });
  } else {
    actions.push({ label: "Open show", tone: "primary", onClick: () => onAct({ kind: "confirmed", bandId: i.bandId!, showId: i.showId! }) });
  }

  return (
    <DetailsFrame title={group ? `Soft hold ×${chip.items.length}` : i.venue ?? "Deal"} anchor={anchor} onClose={onClose}>
      <div className={base.scroll}>
        <header className={base.head}>
          <div className={base.headRow}>
            <span className={styles.statePill} data-kind={chip.kind}>{STATE_LABEL[chip.kind]}</span>
            <button type="button" className={base.close} onClick={onClose} aria-label="Close">
              <Icon icon={X} size={20} />
            </button>
          </div>
          <span className={styles.popArtist}>
            <ArtistMark name={i.artistName} bandId={i.bandId} avatarMediaId={i.artistAvatarMediaId} size="xs" />
            {i.artistName}
          </span>
          <h2 className={styles.popVenue}>{group ? `Soft hold ×${chip.items.length}` : i.venue?.trim() || "TBD"}</h2>
          <p className={base.date}>{when}</p>
        </header>
        <div className={base.sections}>
          {group ? (
            <ul className={styles.groupList} aria-label="Soft holds this night">
              {chip.items.map((x) => (
                <li key={x.offerId}>
                  <span className={base.strong}>{x.venue?.trim() || "TBD"}</span>
                  {x.amountCents != null && (
                    <span className={base.sub}> · {offerDealLine(x.amountCents, x.dealType ?? "guarantee", x.doorSplitPct ?? null)}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            rows.length > 0 && (
              <dl className={styles.rows}>
                {rows.map((r) => (
                  <div key={r.k}>
                    <dt>{r.k}</dt>
                    <dd data-urgent={r.urgent || undefined}>{r.v}</dd>
                  </div>
                ))}
              </dl>
            )
          )}
          {(chip.kind === "hold" || chip.kind === "ready") && team.length > 0 && (
            <section className={styles.team} aria-label="Team">
              <span className={styles.teamLabel}>Team</span>
              <span className={styles.faces}>
                {team.map((p) => (
                  <span key={p.personId} className={styles.face} data-status={p.status} title={`${p.name} — ${p.status}`}>
                    <PersonAvatar personId={p.personId} avatarMediaId={p.avatarMediaId} initials={initials(p.name)} size="xs" />
                  </span>
                ))}
              </span>
              <span className={base.sub}>{inCount} of {team.length} in</span>
            </section>
          )}
        </div>
      </div>
      <footer className={base.actions}>
        <div className={base.buttons}>
          {actions.map((a) => (
            <button
              key={a.label}
              type="button"
              className={a.tone === "danger" ? `${base.btnGhost} ${styles.dangerText}` : a.tone === "brand" ? base.btnDanger : base.btnPrimary}
              onClick={a.onClick}
            >
              {a.label}
            </button>
          ))}
        </div>
      </footer>
    </DetailsFrame>
  );
}

// ---- dates ------------------------------------------------------------------------------------------------

function monthStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 12);
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1, 12);
}
function isoDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
/** The six-row grid: the Sunday on or before the 1st, for 42 days. */
function gridDays(cursor: Date): Date[] {
  const first = monthStart(cursor);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i, 12));
}
function gridWindow(cursor: Date): { from: string; to: string } {
  const days = gridDays(cursor);
  return { from: isoDay(days[0]!), to: isoDay(days[days.length - 1]!) };
}
